<script>
/**
 * @component RolePermissions
 * @description Gestión de permisos por rol. Permite ver y editar qué permisos tiene cada rol.
 * @example <RolePermissions />
 */
import roleService from "@/services/role";
import Spinner from "@/components/ui/Spinner";

const PERMISSION_GROUPS = [
  {
    label: "Pacientes",
    keys: [
      "patient.create",
      "patient.read",
      "patient.query",
      "patient.update",
      "patient.view",
    ],
  },
  {
    label: "Protocolos",
    keys: ["treatment.view", "treatment.assign", "treatment.unassign"],
  },
  {
    label: "Eventos clínicos",
    keys: ["event.create", "event.update"],
  },
  {
    label: "Indicaciones",
    keys: ["indication.create", "indication.update"],
  },
  {
    label: "Validaciones",
    keys: [
      "validation.create",
      "validation.read",
      "validation.query",
      "validation.update",
    ],
  },
  {
    label: "Prescripciones",
    keys: ["prescription.create", "prescription.read", "prescription.query"],
  },
  {
    label: "Agenda",
    keys: [
      "appointment.create",
      "appointment.read",
      "appointment.query",
      "appointment.update",
    ],
  },
  {
    label: "Afiliados",
    keys: [
      "member.create",
      "member.read",
      "member.query",
      "member.update",
      "member.remove",
    ],
  },
];

const HIDDEN_ROLES = ["Administrador", "Paciente"];

export default {
  name: "RolePermissions",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      roles: [],
      allPermissions: [],
      selectedRoleId: null,
      selectedPermissionIds: [],
      permissionGroups: PERMISSION_GROUPS,
    };
  },

  computed: {
    /**
     * @description Roles filtrados (sin admin ni paciente)
     * @returns {Array}
     */
    filteredRoles() {
      return this.roles.filter((r) => !HIDDEN_ROLES.includes(r.name));
    },

    /**
     * @description Rol seleccionado actualmente
     * @returns {Object|null}
     */
    selectedRole() {
      return this.roles.find((r) => r._id === this.selectedRoleId) || null;
    },

    /**
     * @description Mapa permiso._id => permiso para lookups rápidos
     * @returns {Object}
     */
    permissionMap() {
      const map = {};
      this.allPermissions.forEach((p) => {
        map[p._id] = p;
      });
      return map;
    },

    /**
     * @description Mapa permiso.key => permiso._id para lookups por key
     * @returns {Object}
     */
    permissionKeyToId() {
      const map = {};
      this.allPermissions.forEach((p) => {
        const key = p.key || p._id;
        map[key] = p._id;
      });
      return map;
    },

    /**
     * @description Grupos con permisos resueltos contra allPermissions
     * @returns {Array}
     */
    resolvedGroups() {
      return this.permissionGroups
        .map((group) => ({
          label: group.label,
          permissions: group.keys
            .map((key) => {
              const perm = this.allPermissions.find(
                (p) => p._id === key || p.key === key
              );
              return perm ? { ...perm, displayKey: key } : null;
            })
            .filter(Boolean),
        }))
        .filter((g) => g.permissions.length > 0);
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    /**
     * @description Carga roles y permisos del backend
     */
    async fetchData() {
      this.isLoading = true;
      try {
        const [roles, permissions] = await Promise.all([
          roleService.getRoles(),
          roleService.getPermissions(),
        ]);
        this.roles = Array.isArray(roles) ? roles : roles.docs || [];
        this.allPermissions = Array.isArray(permissions)
          ? permissions
          : permissions.docs || [];

        if (this.filteredRoles.length > 0 && !this.selectedRoleId) {
          this.selectRole(this.filteredRoles[0]._id);
        }
      } catch (e) {
        this.$message.error("Error al cargar roles y permisos");
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Selecciona un rol y carga sus permisos activos
     * @param {string} roleId - ID del rol
     */
    selectRole(roleId) {
      this.selectedRoleId = roleId;
      const role = this.roles.find((r) => r._id === roleId);
      if (role && role.permissions) {
        this.selectedPermissionIds = role.permissions.map((p) =>
          typeof p === "string" ? p : p._id
        );
      } else {
        this.selectedPermissionIds = [];
      }
    },

    /**
     * @description Verifica si un permiso está activo para el rol seleccionado
     * @param {string} permId - ID del permiso
     * @returns {boolean}
     */
    isPermissionActive(permId) {
      return this.selectedPermissionIds.includes(permId);
    },

    /**
     * @description Toggle de un permiso
     * @param {string} permId - ID del permiso
     */
    togglePermission(permId) {
      const idx = this.selectedPermissionIds.indexOf(permId);
      if (idx >= 0) {
        this.selectedPermissionIds.splice(idx, 1);
      } else {
        this.selectedPermissionIds.push(permId);
      }
    },

    /**
     * @description Guarda los permisos del rol seleccionado
     */
    async savePermissions() {
      if (!this.selectedRoleId) return;
      this.isSaving = true;
      try {
        const updated = await roleService.updatePermissions(
          this.selectedRoleId,
          this.selectedPermissionIds
        );
        const idx = this.roles.findIndex((r) => r._id === this.selectedRoleId);
        if (idx >= 0 && updated) {
          this.$set(this.roles, idx, updated);
        }
        this.$message.success("Permisos actualizados");
      } catch (e) {
        this.$message.error("Error al guardar permisos");
        console.error(e);
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Devuelve label legible de un key de permiso
     * @param {string} key - Key del permiso (ej: "patient.create")
     * @returns {string}
     */
    getPermissionLabel(key) {
      const parts = key.split(".");
      const action = parts[parts.length - 1];
      const map = {
        create: "Crear",
        read: "Leer",
        query: "Listar",
        update: "Editar",
        remove: "Eliminar",
        view: "Ver",
        assign: "Asignar",
        unassign: "Desasignar",
      };
      return map[action] || action;
    },
  },
};
</script>

<template lang="pug">
section
  spinner(v-if="isLoading")
  template(v-else)
    .role-permissions__info
      micon(name="info")
      span Los cambios de permisos se aplican cuando el usuario cierra sesión y vuelve a ingresar.

    .role-permissions__layout
      //- Role tabs
      .role-permissions__roles
        .role-permissions__role-item(
          v-for="role in filteredRoles"
          :key="role._id"
          :class="{ 'is-active': selectedRoleId === role._id }"
          @click="selectRole(role._id)"
        ) {{ role.name }}

      //- Permission groups
      .role-permissions__content(v-if="selectedRole")
        .role-permissions__group(v-for="group in resolvedGroups" :key="group.label")
          .role-permissions__group-label {{ group.label }}
          .role-permissions__group-perms
            .role-permissions__perm(v-for="perm in group.permissions" :key="perm._id")
              el-switch(
                :value="isPermissionActive(perm._id)"
                @change="togglePermission(perm._id)"
                active-color="#00c4a1"
              )
              span.role-permissions__perm-label {{ getPermissionLabel(perm.displayKey) }}
                span.role-permissions__perm-key  ({{ perm.displayKey }})

        .role-permissions__actions
          el-button(type="primary" :loading="isSaving" @click="savePermissions") Guardar permisos
</template>

<style lang="scss" scoped>
.role-permissions__info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #92400e;

  .micon {
    fill: #92400e;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
}

.role-permissions__layout {
  display: flex;
  gap: 24px;
}

.role-permissions__roles {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.role-permissions__role-item {
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: $text-secondary-new;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
  }

  &.is-active {
    background: $teal-light;
    color: $navy;
    font-weight: 600;
  }
}

.role-permissions__content {
  flex: 1;
  min-width: 0;
}

.role-permissions__group {
  margin-bottom: 20px;
}

.role-permissions__group-label {
  font-size: 14px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid $border-color;
}

.role-permissions__group-perms {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
}

.role-permissions__perm {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.role-permissions__perm-label {
  font-size: 13px;
  color: $text-primary-new;
}

.role-permissions__perm-key {
  font-size: 11px;
  color: $text-secondary-new;
}

.role-permissions__actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid $border-color;
}
</style>
